import type { Database } from '@ctrl/db';
import { parseTheme, type StorefrontTheme } from '@ctrl/sections/theme';

import { adminApi, type DomainState } from '../admin-api.ts';
import { supabase } from '../supabase.ts';

/** Postgres `jsonb`, as the generated types name it. */
type Json = Database['public']['Tables']['site_settings']['Row']['seo'];

/**
 * The storefront's own settings: theme, the chrome copy around it, and the
 * addresses it answers on.
 *
 * `storefront_sites.theme` is a JSON blob validated by `ThemeSchema`, so it
 * is parsed on read (every leaf has a fallback, so a partial or older theme
 * still opens) and written whole.
 */

export interface SiteRecord {
  id: string;
  name: string;
  templateSlug: string | null;
  published: boolean;
  theme: StorefrontTheme;
}

export async function getSite(accountId: string): Promise<SiteRecord | null> {
  const { data, error } = await supabase
    .from('storefront_sites')
    .select('id, name, template_slug, published, theme')
    .eq('account_id', accountId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    id: data.id,
    name: data.name,
    templateSlug: data.template_slug,
    published: data.published,
    theme: parseTheme(data.theme),
  };
}

export async function saveTheme(
  accountId: string,
  siteId: string,
  theme: StorefrontTheme,
): Promise<void> {
  const { error } = await supabase
    .from('storefront_sites')
    .update({ theme: theme as unknown as Json })
    .eq('account_id', accountId)
    .eq('id', siteId);

  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/* Site settings                                                               */
/* -------------------------------------------------------------------------- */

export interface ChatSettings {
  greeting: string;
  awayMessage: string;
  agentName: string;
  askForEmail: boolean;
}

export interface BlogSettings {
  title: string;
  description: string;
}

export interface SiteSettingsRecord {
  ageGateEnabled: boolean;
  ageGateCopy: string;
  announcement: string;
  cartBehavior: 'drawer' | 'page';
  requireAccount: boolean;
  chatEnabled: boolean;
  chat: ChatSettings;
  blogEnabled: boolean;
  blog: BlogSettings;
  seoTitle: string;
  seoDescription: string;
  social: Record<string, string>;
  /** Resolved from `logo_media_id` / `favicon_media_id` in the library. */
  logo: { id: string; url: string } | null;
  favicon: { id: string; url: string } | null;
}

export const SOCIAL_KEYS = ['instagram', 'facebook', 'x', 'tiktok', 'youtube'] as const;

const DEFAULT_CHAT: ChatSettings = {
  greeting: 'Hi! Ask us anything about the menu.',
  awayMessage: 'We are closed right now, but leave a message and we will reply.',
  agentName: 'The shop',
  askForEmail: true,
};

const DEFAULT_BLOG: BlogSettings = { title: 'Journal', description: '' };

export const EMPTY_SITE_SETTINGS: SiteSettingsRecord = {
  ageGateEnabled: true,
  ageGateCopy: '',
  announcement: '',
  cartBehavior: 'drawer',
  requireAccount: false,
  chatEnabled: false,
  chat: DEFAULT_CHAT,
  blogEnabled: false,
  blog: DEFAULT_BLOG,
  seoTitle: '',
  seoDescription: '',
  social: {},
  logo: null,
  favicon: null,
};

export async function getSiteSettings(accountId: string): Promise<SiteSettingsRecord> {
  const { data, error } = await supabase
    .from('site_settings')
    .select(
      'age_gate_enabled, age_gate_copy, announcement, checkout_settings, chat_enabled, chat_settings, blog_enabled, blog_settings, seo, social, logo_media_id, favicon_media_id',
    )
    .eq('account_id', accountId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return EMPTY_SITE_SETTINGS;

  const checkout = (data.checkout_settings ?? {}) as {
    cartBehavior?: string;
    requireAccount?: boolean;
  };
  const chat = (data.chat_settings ?? {}) as Partial<ChatSettings>;
  const blog = (data.blog_settings ?? {}) as Partial<BlogSettings>;
  const seo = (data.seo ?? {}) as { title?: string; description?: string };

  return {
    ageGateEnabled: data.age_gate_enabled,
    ageGateCopy: data.age_gate_copy ?? '',
    announcement: data.announcement ?? '',
    cartBehavior: checkout.cartBehavior === 'page' ? 'page' : 'drawer',
    requireAccount: Boolean(checkout.requireAccount),
    chatEnabled: data.chat_enabled,
    chat: { ...DEFAULT_CHAT, ...chat },
    blogEnabled: data.blog_enabled,
    blog: { ...DEFAULT_BLOG, ...blog },
    seoTitle: seo.title ?? '',
    seoDescription: seo.description ?? '',
    social: (data.social ?? {}) as Record<string, string>,
    logo: await resolveAsset(accountId, data.logo_media_id),
    favicon: await resolveAsset(accountId, data.favicon_media_id),
  };
}

/**
 * The library row behind a stored id.
 *
 * The columns keep an id rather than a URL, so the picker needs the address
 * back to show what is currently set. Missing means the asset was deleted
 * from the library since -- the setting is then simply empty, which is what
 * the storefront will see too.
 */
async function resolveAsset(
  accountId: string,
  assetId: string | null,
): Promise<{ id: string; url: string } | null> {
  if (!assetId) return null;

  const { data, error } = await supabase
    .from('media_assets')
    .select('id, public_url')
    .eq('account_id', accountId)
    .eq('id', assetId)
    .maybeSingle();

  if (error) throw error;

  return data?.public_url ? { id: data.id, url: data.public_url } : null;
}

type SettingsPatch = Partial<{
  age_gate_enabled: boolean;
  age_gate_copy: string | null;
  announcement: string | null;
  checkout_settings: Json;
  chat_enabled: boolean;
  chat_settings: Json;
  blog_enabled: boolean;
  blog_settings: Json;
  seo: Json;
  social: Json;
  logo_media_id: string | null;
  favicon_media_id: string | null;
}>;

/**
 * Update, then insert if the account has no settings row yet.
 *
 * Not an upsert: `site_settings` grants UPDATE column by column, and
 * `ON CONFLICT DO UPDATE` is checked against the table's privilege instead --
 * it answers "permission denied" to a member entitled to every column here.
 */
export async function saveSiteSettings(accountId: string, patch: SettingsPatch): Promise<void> {
  const { data, error } = await supabase
    .from('site_settings')
    .update(patch)
    .eq('account_id', accountId)
    .select('account_id');

  if (error) throw error;
  if (data?.length) return;

  const { error: insertError } = await supabase
    .from('site_settings')
    .insert({ account_id: accountId, ...patch });

  if (insertError) throw insertError;
}

/* -------------------------------------------------------------------------- */
/* Domains                                                                     */
/* -------------------------------------------------------------------------- */

export interface DomainRecord {
  id: string;
  hostname: string;
  kind: 'subdomain' | 'custom';
  status: string;
  isPrimary: boolean;
  /** DNS the merchant must publish: ownership first, then the certificate. */
  verificationRecords: Array<{
    kind?: 'ownership' | 'certificate';
    type?: string;
    name?: string;
    value?: string;
  }>;
  error: string | null;
}

export async function listDomains(accountId: string): Promise<DomainRecord[]> {
  const { data, error } = await supabase
    .from('storefront_domains')
    .select('id, hostname, kind, status, is_primary, verification_records, error')
    .eq('account_id', accountId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    hostname: row.hostname,
    kind: row.kind as DomainRecord['kind'],
    status: row.status,
    isPrimary: row.is_primary,
    verificationRecords: Array.isArray(row.verification_records)
      ? (row.verification_records as DomainRecord['verificationRecords'])
      : [],
    error: row.error,
  }));
}

export async function rootDomain(): Promise<string> {
  const { data, error } = await supabase.rpc('storefront_root_domain');

  if (error) throw error;

  return data ?? 'cannactrl.app';
}

export async function subdomainAvailable(candidate: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_subdomain_available', { candidate });

  if (error) throw error;

  return Boolean(data);
}

/**
 * Claims `<candidate>.<root>` and makes it primary if nothing else is.
 *
 * Through the API rather than straight to the RPC: claiming changes which
 * hostname resolves, and that map lives in KV. A browser calling the RPC
 * itself would write a row that reads correctly in this screen and serve a
 * 404 at the hostname it names.
 */
export async function claimSubdomain(accountId: string, candidate: string): Promise<void> {
  await adminApi.domains.claimSubdomain(accountId, candidate);
}

/**
 * Records a custom domain and attaches it to the zone.
 *
 * Through the API because attaching is a Cloudflare call with a server-side
 * token. The row comes back `pending` or `verifying` carrying the DNS the
 * merchant has to publish; it does not resolve until `checkDomain` sees both
 * ownership and the certificate go active.
 */
export async function addCustomDomain(accountId: string, hostname: string): Promise<void> {
  await adminApi.domains.addCustom(accountId, hostname);
}

/**
 * Re-reads Cloudflare and syncs the row.
 *
 * Also the retry: a domain whose attach failed has no hostname id, and this
 * attaches it now.
 */
export async function checkDomain(accountId: string, id: string): Promise<DomainState> {
  return adminApi.domains.check(accountId, id);
}

export async function setPrimaryDomain(accountId: string, id: string): Promise<void> {
  const { error: clearError } = await supabase
    .from('storefront_domains')
    .update({ is_primary: false })
    .eq('account_id', accountId)
    .eq('is_primary', true);

  if (clearError) throw clearError;

  const { error } = await supabase
    .from('storefront_domains')
    .update({ is_primary: true })
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

/** Removes the row and unbinds the hostname. Custom domains only, per RLS. */
export async function deleteDomain(accountId: string, id: string): Promise<void> {
  await adminApi.domains.remove(accountId, id);
}
